<template>
  <div class="input-combobox w-full">
    <Combobox
      as="div"
      v-bind="$attrs"
      v-model="selectedItem"
      :class="$attrs.disabled || preventInput ? 'pointer-events-none select-none' : ''"
      @update:model-value="onModelValueUpdate"
      v-slot="{ open }"
    >
      <!-- Experimental hack to capture open state from combobox internals -->
      <div v-if="captureOpen(open)" />

      <div
        class="surface relative rounded-md"
        :style="{ marginBottom: isOpenComputed && props.grow ? `${comboboxOptionsHeight}px` : '' }"
      >
        <ComboboxLabel v-if="label" class="block text-sm font-medium">{{ label }}</ComboboxLabel>
        <!-- Custom flex container for before-input slot with wrapping -->
        <div
          v-if="$slots['before-input']"
          ref="beforeInputSlotWrapper"
          class="absolute top-1/2 left-2 z-10 -translate-y-1/2 transform"
          :class="
            $attrs.disabled
              ? 'dark:bg-opacity-5! cursor-not-allowed! bg-gray-50! text-gray-400!'
              : ''
          "
        >
          <slot name="before-input" />
        </div>
        <div
          class="flex flex-wrap items-center gap-x-2 gap-y-1"
          :class="$slots['inline-input'] ? 'p-1 pr-9' : ''"
        >
          <slot name="inline-input" />
          <ComboboxInput
            autocomplete="off"
            :placeholder="inputPlaceholder"
            class="surface focus:border-secondary focus:ring-secondary w-full flex-1 overscroll-y-contain rounded-md border-none px-2 py-1 leading-none focus:ring-2 sm:text-sm placeholder:sm:text-sm"
            :class="[
              inputClass,
              $slots['before-input'] ? 'pl-8' : '',
              $slots['custom-selected-item'] && selectedItem && !hasFocus
                ? 'text-transparent!'
                : '',
              $attrs.disabled
                ? 'dark:bg-opacity-5! cursor-not-allowed! bg-gray-50! text-gray-400!'
                : ''
            ]"
            draggable="false"
            :style="comboboxHeight ? { height: comboboxHeight } : {}"
            @change="onComboboxInput"
            @click="(e: any) => ($attrs.disabled ? undefined : onInputClicked(e))"
            :value="displayValue"
            @keydown="onComboboxKeydown"
            @keyup="onComboboxKeyup"
            @focusin="onFocusIn"
            @focusout="onFocusOut"
            @mousedown="onComboboxMousedown"
            @mouseup="onComboboxMouseup"
            ref="input"
            :tabindex="$attrs.disabled || preventInput ? '-1' : $attrs.tabindex"
          />
        </div>

        <!-- Error / loading icon -->
        <div
          v-if="errors?.length > 0 || isLoading"
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-x-2 pr-8"
        >
          <MultiBadge
            v-if="isLoading"
            size="xs"
            color="secondary"
            class="flex items-center gap-x-1"
          >
            <LoadingSpinner class="size-3" />
            <slot name="loading-text">Gegevens laden...</slot>
          </MultiBadge>

          <FontAwesomeIcon
            v-if="errors?.length > 0"
            :icon="['fas', 'circle-exclamation']"
            size="lg"
            class="text-warning"
          />
        </div>

        <!-- Custom display of selected item, superimposed on combobox -->
        <div
          v-if="$slots['custom-selected-item']"
          :class="[
            $attrs.disabled || preventInput ? 'pointer-events-auto' : '',
            $attrs.disabled ? 'cursor-not-allowed! text-gray-400!' : '',
            wrapCustomSelectedItem ? 'break-words' : 'truncate'
          ]"
          class="pointer-events-none absolute top-0 w-full rounded-md py-1 pr-8.5 pl-2 sm:text-sm"
          ref="customItemSlotWrapper"
        >
          <slot
            v-if="selectedItem && (!hasFocus || selectedText.length === 0)"
            name="custom-selected-item"
            :item="selectedItem"
          />
        </div>

        <ComboboxButton
          v-if="showButton"
          @click="!$attrs.disabled ? onComboboxButtonClick() : undefined"
          class="surface absolute inset-y-0.5 right-px flex items-center rounded-r-md px-2 focus:outline-hidden"
          :class="[$attrs.disabled ? 'opacity-30' : '']"
          ref="comboboxButton"
        >
          <FontAwesomeIcon
            :icon="['fas', isOpenComputed ? 'chevron-up' : 'chevron-down']"
            size="sm"
          />
        </ComboboxButton>

        <div v-show="isOpenComputed">
          <ComboboxOptions
            static
            ref="comboboxOptions"
            :key="comboboxOptionsKey"
            v-if="filteredItems.length > 0 || creationOption || slots['custom-options']"
            @mousedown.left="optionLmbDown = true"
            @mouseup.left="optionLmbDown = false"
            @mouseenter="isAllOptionsDisabled = false"
            @scrollend="$emit('scrollend')"
            class="input-combobox__options surface absolute z-40 mt-1 max-h-64 w-full overflow-y-auto rounded-md border-none py-1 text-sm shadow-lg focus:outline-hidden"
          >
            <ComboboxOption class="input-combobox__option" v-if="slots['top-of-options']" disabled>
              <slot name="top-of-options" />
            </ComboboxOption>

            <ComboboxOption
              v-for="item in filteredItems"
              :key="item[itemsKeyName]"
              :value="item"
              :disabled="isDisabled(item) || isAllOptionsDisabled"
              as="template"
              @mousedown.left="optionLmbDown = true"
              @mouseup.left="optionLmbDown = false"
              v-slot="{ active, selected }"
              class="input-combobox__option input-combobox__option--item"
              :class="{ 'blur-xs': blurItems }"
            >
              <li class="relative w-full cursor-default break-words select-none">
                <slot
                  v-if="slots['custom-list-item']"
                  name="custom-list-item"
                  :item="item"
                  :active="active"
                  :disabled="isDisabled(item) || isAllOptionsDisabled"
                />

                <span
                  v-else
                  class="block py-2 pl-2"
                  :class="{
                    'font-semibold': selected,
                    'primary cursor-pointer': active,
                    'dark:bg-opacity-5! cursor-not-allowed! bg-gray-50! text-gray-400!':
                      isDisabled(item)
                  }"
                >
                  {{
                    getItemValue(item, {
                      active,
                      selected,
                      disabled: isDisabled(item)
                    })
                  }}
                </span>
              </li>
            </ComboboxOption>

            <slot
              name="custom-options"
              v-bind="{
                disabled: isAllOptionsDisabled,
                onMouseDownLeft: () => (optionLmbDown = true),
                onMouseUpLeft: () => (optionLmbDown = false)
              }"
            />

            <!-- CREATE NEW ITEM OPTION -->
            <ComboboxOption
              v-if="creationOption"
              v-slot="{ active, selected }"
              @mousedown.left="optionLmbDown = true"
              @mouseup.left="optionLmbDown = false"
              :disabled="isAllOptionsDisabled"
            >
              <li
                :class="[
                  'relative flex cursor-default items-center px-1 py-2 select-none',
                  active ? 'bg-primary text-on-primary' : 'surface'
                ]"
              >
                <FontAwesomeIcon :icon="['fas', 'plus']" size="xs" class="mr-1" />
                <span :class="['block truncate', selected && 'font-semibold']">
                  {{ newItemText || "Nieuw item" }}
                </span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </div>
      </div>

      <!-- Error message -->
      <p v-if="errors.length > 0" class="text-error mt-2 text-sm">
        {{ errors[0].$message }}
      </p>

      <slot />
    </Combobox>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  type Ref,
  ref,
  type Slots,
  useSlots,
  useTemplateRef,
  watch,
  watchEffect
} from "vue"
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions
} from "@headlessui/vue"
import { useEventListener, useMagicKeys, useMouseInElement, useTextSelection } from "@vueuse/core"
import { useModelFormStore } from "@/store/model-forms.store"
import type { BaseModel } from "@/models/base.model"
import type { ErrorObject } from "@vuelidate/core"
import { ModelFactory } from "@/factories/model-factory"
import { CustomComboboxOptionWrapper } from "@/components/inputs/input_combobox/CustomComboboxOptionWrapper"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue"
import MultiBadge from "@/components/misc/MultiBadge.vue"

const props = withDefaults(
  defineProps<{
    /** The current value of the combobox, can be string, object, number, or null. */
    modelValue?: string | object | number | null | undefined
    /** The array of items to display in the combobox options. */
    items: any[]
    /** The key name in each item object to use as the unique identifier. */
    itemsKeyName: string
    /** The key name to display for each item, if not using a custom function. */
    itemsValueName?: string
    /** Function to determine the display value of an item, given its status options. */
    itemsValueFunction?: (
      item: any,
      options: { active?: boolean; selected?: boolean; disabled?: boolean }
    ) => string
    /** Text to display in the combobox on mount. */
    textOnMounted?: string | null
    /** If true, returns a string instead of null when no matches are found. */
    returnStringIfNoMatches?: boolean
    /** If true, returns null if no matches are found. */
    returnNullIfNoMatches?: boolean
    /** If true, returns null when focus is lost with no matches. */
    returnNullOnFocusOutAndNoMatches?: boolean
    /** Determines whether to show the dropdown button next to the input. */
    showButton?: boolean
    /** Label text for the combobox. */
    label?: string
    /** Custom CSS class for the combobox input element. */
    inputClass?: string
    /** Placeholder text for the combobox input. */
    inputPlaceholder?: string
    /** Minimum number of characters required in the query to show options. */
    minCharactersRequired?: number
    /** Enables thorough search through item values. */
    searchThoroughly?: boolean
    /** Allows creation of a new item option if no matches are found. */
    creationOption?: boolean
    /** Type of model to use for creating a new item. */
    creationModelType?: ModelConstructorSignature<any, any>
    /** Parameters to use when creating a new item. */
    creationParameters?: Record<string, unknown>
    /** If true, automatically selects newly created item after creation. */
    autoSelectNewlyCreatedModel?: boolean
    /** Indicates if the combobox input is editable. */
    editable?: boolean
    /** Custom path for editing object in model. */
    customEditObjPath?: string | null
    /** Function to convert a selected model to a base model when editing. */
    editModelConversionFunction?: (selectedModel: any) => BaseModel | undefined
    /** Maximum number of items to display in the combobox options. */
    maxItemCount?: number
    /** Sorts the options alphabetically if set to true. */
    sortAlphabetically?: boolean
    /** Function to display a custom selected item. */
    selectedItemDisplayFn?: (item: any) => string | undefined
    /** Custom sorting function for items. */
    itemsSortFn?: (a: any, b: any) => number
    /** Custom filtering function for items. */
    itemsFilterFn?: (item: any, query: string) => boolean
    /** Custom include criterium function for items. */
    includeCriteriumFn?: (item: any, query: string) => boolean
    /** Prevents input from being editable if set to true. */
    preventInput?: boolean
    /** Array of error messages to display below the combobox. */
    errors?: ErrorObject[]
    /** Wraps custom selected item display in text wrapping mode. */
    wrapCustomSelectedItem?: boolean
    /** Automatically focuses on the combobox input on load if set to true. */
    autoFocus?: boolean
    /** Auto-selects an item if it matches the query exactly. */
    autoSelectOnExactMatch?: boolean
    /** Shows the options dropdown on focus if true. */
    showOptionsOnFocus?: boolean
    /** Shows the options dropdown on click if true. */
    showOptionsOnClick?: boolean
    /** Array of items to disable in the options. */
    disabledItems?: any[]
    /** If true, inactivates the active option when the query is empty. */
    inactivateActiveOptionOnEmptyQuery?: boolean
    /** Expands combobox height to fit options if set to true. */
    grow?: boolean
    /** If true, shows a loading spinner */
    isLoading?: boolean
    /** Hook into the start of the onModelValueUpdate function. Allows canceling item selection altogether. */
    beforeModelValueUpdateHandler?: (val: any) => boolean
    /** Blur filtered items (e.g. for showing items being loaded state) */
    blurItems?: boolean
    newItemText?: string
  }>(),
  {
    modelValue: null,
    returnStringIfNoMatches: false,
    returnNullIfNoMatches: true,
    returnNullOnFocusOutAndNoMatches: false,
    showButton: true,
    minCharactersRequired: 0,
    searchThoroughly: false,
    creationOption: false,
    creationParameters: () => ({}),
    autoSelectNewlyCreatedModel: true,
    editable: true,
    customEditObjPath: null,
    maxItemCount: 50,
    sortAlphabetically: true,
    preventInput: false,
    errors: () => [],
    wrapCustomSelectedItem: true,
    autoFocus: false,
    autoSelectOnExactMatch: false,
    showOptionsOnFocus: false,
    showOptionsOnClick: false,
    inactivateActiveOptionOnEmptyQuery: true,
    grow: false,
    isLoading: false,
    beforeModelValueUpdateHandler: () => true,
    blurItems: false
  }
)

const emit = defineEmits([
  "update:modelValue",
  "itemCreated",
  "itemUpdated",
  "itemSelected",
  "isCreatingNewItem",
  "itemDeselected",
  "queryChanged",
  "comboboxOptionsOpenChange",
  "scrollend"
])

const slots: Slots = useSlots()

defineOptions({
  inheritAttrs: false
})

const input = ref()
const customItemSlotWrapper = useTemplateRef("customItemSlotWrapper")
const comboboxOptions = useTemplateRef("comboboxOptions")

const modelFormStore = useModelFormStore()

const comboboxOptionsHeight = ref(0)
const comboboxOptionsKey = ref()

const query = ref(props.textOnMounted ? props.textOnMounted : "")
const hasFocus = ref(false)
const lmbDown = ref(false)
const rmbDown = ref(false)
const optionLmbDown = ref(false)
const selectedItem = ref()

const enterKeyDown = ref(false)
const anyKeyDown = ref(false)

const isAllOptionsDisabled = ref(false)

// Convert disabled items array to dictionary for constant time lookup.
const disabledItemsDict = computed(() => {
  const dict: any = {}
  if (props.disabledItems) {
    for (const item of props.disabledItems) {
      dict[item[props.itemsKeyName]] = true
    }
  }
  return dict
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let doubleClickTimer: ReturnType<typeof setTimeout> | null = null
let nextClickIsDoubleClick = false

const selectedText = computed(() => useTextSelection().text.toString())

const customHeight: Ref<number | null> = ref(null)
let resizeObserver: ResizeObserver | null = null

const filteredItems: any = computed(() => {
  if (!isOpenComputed.value) {
    return [] // Increase performance by not filtering when the combobox results are not visible.
  }

  if (query.value.length < props.minCharactersRequired) {
    if (selectedItem.value) {
      return [selectedItem.value]
    }
    return []
  }

  const filtered = []

  for (let i = 0, count = 0; i < props.items.length && count < props.maxItemCount; i++) {
    const item = props.items[i]
    let includeCriteriumIsMet

    if (props.searchThoroughly) {
      const superString = getItemValue(item).toLowerCase()
      const substrings = query.value.toString().toLowerCase().split(" ")
      includeCriteriumIsMet = substrings.every((substring) => superString.includes(substring))
    } else if (props.itemsFilterFn) {
      includeCriteriumIsMet = props.itemsFilterFn(item, query.value)
    } else if (props.includeCriteriumFn) {
      includeCriteriumIsMet = props.includeCriteriumFn(item, query.value)
    } else {
      includeCriteriumIsMet = getItemValue(item)
        .toLowerCase()
        .includes(query.value.toString().toLowerCase())
    }

    if (includeCriteriumIsMet) {
      filtered.push(item)
      count++
    }
  }

  let sorted = filtered
  if (props.sortAlphabetically) {
    sorted = filtered.sort((a: any, b: any) => {
      const aValue = getItemValue(a).toLowerCase()
      const bValue = getItemValue(b).toString().toLowerCase()

      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
      return 0
    })
  }

  if (props.itemsSortFn) {
    return sorted.sort(props.itemsSortFn)
  }

  return sorted
})

const displayValue = computed(() => {
  if (!selectedItem.value) {
    return query.value
  }
  if (props.selectedItemDisplayFn) {
    return props.selectedItemDisplayFn(selectedItem.value) ?? ""
  }
  return getItemValue(selectedItem.value)
})

// // WAG-660: In mobile view the focus jumps to the instruction column when focusing on combobox service provider+machine
// const truncatedDisplayValue = computed(() => {
//   if (input.value) {
//     const inputWidth = input.value.$el.offsetWidth
//     const textWidth = getTextWidth(displayValue.value)
//     if (textWidth > inputWidth) {
//       return truncateText(displayValue.value, inputWidth)
//     }
//   }
//   return displayValue.value
// })

// // Helper function to get the width of the text
// function getTextWidth(text: string) {
//   // Hack: Create a temporary span element to measure the width of the text, this can't be done in a input
//   const span = document.createElement("span")
//   span.textContent = text
//   span.style.whiteSpace = "nowrap"
//   span.style.visibility = "hidden"
//   document.body.appendChild(span)
//   const width = span.offsetWidth
//   document.body.removeChild(span)
//   return width
// }

// // Helper function to truncate text if it exceeds a certain width
// function truncateText(text: string, maxWidth: number) {
//   let truncatedText = text
//   while (getTextWidth(truncatedText + "...") > maxWidth - 24) {
//     truncatedText = truncatedText.slice(0, -1)
//   }
//   return truncatedText + "..."
// }

// Start: Calculate the height of the combobox
const comboboxRowHeight = 28

const comboboxHeight = computed(() => {
  if (customHeight.value) {
    if (customHeight.value < comboboxRowHeight) {
      return null
    }
    return customHeight.value + "px"
  }
  return null
})

watch(customItemSlotWrapper, (val) => {
  if (props.wrapCustomSelectedItem && val) {
    resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height
      // if it's more then 1 line
      if (height > comboboxRowHeight) {
        customHeight.value = height + 8
      }
    })
    resizeObserver.observe(val)
  } else {
    if (resizeObserver) {
      resizeObserver.disconnect()
    }
  }
})

function recalcCustomHeight() {
  nextTick(() => {
    if (customItemSlotWrapper.value) {
      const newHeight = customItemSlotWrapper.value.offsetHeight
      if (newHeight > comboboxRowHeight) {
        customHeight.value = newHeight + 8
      } else {
        customHeight.value = null
      }
    }
  })
}

useEventListener("resize", recalcCustomHeight)
// End: Calculate the height of the combobox

function onComboboxInput(event: any) {
  /* @change will get triggered by enter key as well, passing event.target.value = "". */
  if (enterKeyDown.value) {
    return
  }
  if (!hasFocus.value) {
    return
  }
  /* This is kind of weird but necessary to allow deletion of selected item text. */
  if (event.target.value === "") {
    query.value = "." // Next if-condition will hit, and this character will be cleared.
  }
  /* If-condition necessary because otherwise, when field loses focus, component will emit string representation. */
  if (event.target.value !== query.value) {
    query.value = event.target.value

    const wasItemSelected = !!selectedItem.value

    selectedItem.value = null

    if (wasItemSelected) {
      // If an item was selected
      onItemDeselected()
    }
  }
}

function onItemDeselected() {
  if (props.returnStringIfNoMatches) {
    emit("update:modelValue", query.value)
    emit("itemDeselected")
  } else if (props.returnNullIfNoMatches) {
    emit("update:modelValue", null)
    emit("itemDeselected")
  } else {
    emit("itemDeselected")
  }
}

function onComboboxButtonClick() {
  query.value = "" // Shows the whole list of options again, instead of filtering on current input.
}

function onInputClicked(event: any) {
  const inputElement = event.target

  inputElement.select()

  if (nextClickIsDoubleClick && selectedItem.value) {
    editSelectedItem()
  }
  nextClickIsDoubleClick = true
  doubleClickTimer = setTimeout(() => {
    nextClickIsDoubleClick = false
  }, 200)

  if (props.showOptionsOnClick) {
    input.value.$el.dispatchEvent(new KeyboardEvent("input"))
  }
}

const { ctrl, enter } = useMagicKeys()
watchEffect(() => {
  // Allow editing of selected item by pressing ctrl+enter.
  if (ctrl.value && enter.value && hasFocus.value) {
    editSelectedItem()
  }
})

function editSelectedItem() {
  if (props.editable) {
    const itemToEdit = props.editModelConversionFunction
      ? props.editModelConversionFunction(selectedItem.value)
      : selectedItem.value
    if (itemToEdit) {
      // Below line is necessary, otherwise editing an existing item and saving will clear the combobox.
      // This is because for some reason, on save, query.value will be empty in onModelValueUpdate().
      query.value = getItemValue(selectedItem.value)

      // If conversion function returned a valid object, open edit form.
      modelFormStore.openForm({
        model: itemToEdit,
        saveHandle: onItemSaved,
        closeHandle: onItemFormClose,
        creationParameters: props.creationParameters
      })
    }
  }
}

function onComboboxKeydown(e: KeyboardEvent) {
  anyKeyDown.value = true
  if (e.key === "Enter") {
    enterKeyDown.value = true
  } else if (e.key === "Escape") {
    blur()
  }
}

function onComboboxKeyup(e: KeyboardEvent) {
  anyKeyDown.value = false
  if (e.key === "Enter") {
    enterKeyDown.value = false
  }
}

function onComboboxMousedown(e: MouseEvent) {
  if (e.button === 0) {
    lmbDown.value = true
  } else if (e.button === 2) {
    rmbDown.value = true
  }
}

function onComboboxMouseup(e: MouseEvent) {
  if (e.button === 0) {
    lmbDown.value = false
  } else if (e.button === 2) {
    rmbDown.value = false
  }
}

function onFocusIn() {
  if (rmbDown.value) {
    return
  }

  hasFocus.value = true

  if (props.showOptionsOnFocus) {
    input.value.$el.dispatchEvent(new KeyboardEvent("input"))
  }
}

function onFocusOut() {
  if (!optionLmbDown.value && !anyKeyDown.value) {
    // Reset text on focus out and no results.
    if (!selectedItem.value && !props.returnStringIfNoMatches) {
      query.value = ""
    }

    // Check if flag is true, item is selected. Additionally, check if user has left mouse down on a select option.
    // Without the latter, this code will hit when user selects an option with the mouse.
    if (props.returnNullOnFocusOutAndNoMatches && !selectedItem.value && !optionLmbDown.value) {
      emit("update:modelValue", null)
    }
  }

  /* If user clicks list item, focusout should not be propagated. */
  // TODO I disabled this because @focusout would not trigger on InstructionCard --> ServiceProviderMachineCombobox.
  // if (optionLmbDown.value && hasFocus.value) {
  //   e.stopPropagation()
  // }

  hasFocus.value = false
  anyKeyDown.value = false
  enterKeyDown.value = false
}

function onItemSaved<ModelType extends BaseModel>(item: ModelType, isCreating = true) {
  if (props.autoSelectNewlyCreatedModel) {
    nextTick(() => selectItem(item))
  }

  // Emit the appropriate event based on whether the item was created or updated.
  // PNTS-2531 Introduced isCreating parameter to allow differentiating between create and update events.
  // Before this parameter, the itemCreated event was emitted for both create and update operations. Consequently,
  // many components do not pass the isCreating parameter, and we default it true so we won't break existing logic.
  if (isCreating) {
    emit("itemCreated", item)
  } else {
    emit("itemUpdated", item)
  }

  nextTick(() => {
    focus()
  })
}

function onItemFormClose() {
  emit("isCreatingNewItem", false)
}

function getItemValue(item: any, options?: any) {
  if (props.itemsValueName) {
    if (props.itemsValueName.includes(".")) {
      /* Support for recursively getting to nested objects (using dot notation) */
      const fields = props.itemsValueName.split(".")
      for (const field of fields) {
        item = item[field]
      }
      return item?.toString() ?? ""
    }
    // Access the property value (works for both regular properties and getters)
    const value = item[props.itemsValueName]
    return value?.toString() ?? ""
  } else if (props.itemsValueFunction) {
    // Function based display text. Pass item to function, along with options object.
    return props.itemsValueFunction(item, {
      active: options?.active,
      selected: options?.selected,
      disabled: isDisabled(item)
    })
  }
}

function initiateNewItemCreation() {
  emit("isCreatingNewItem", true)

  if (!props.creationModelType) {
    throw new Error("If creationOption is true, creationModelType must be set.")
  }

  modelFormStore.openForm({
    model: ModelFactory.createModel(props.creationModelType),
    modelType: props.creationModelType,
    saveHandle: onItemSaved,
    closeHandle: onItemFormClose,
    textInput: query.value,
    creationParameters: props.creationParameters
  })
  selectedItem.value = null
}

function isDisabled(item: any): boolean {
  return !!disabledItemsDict.value[item[props.itemsKeyName]]
}

function focus() {
  nextTick(() => {
    if (!input.value) {
      return // Input could be unmounted in the meantime. E.g. InstructionCard was closed while creating entity.
    }
    if (input.value.$el) {
      input.value.$el.focus()
    }
  })
}

function blur() {
  // Input could be 0 if InputCombobox was unmounted in the meantime. E.g. when blur() was wrapped in a nextTick().
  // That is why we ?-chain.
  input.value?.$el.blur()
}

function setForceOpenState(state: boolean) {
  forceOpen.value = state
}

function selectItem(item: any) {
  // Simply wrapping handleModelValueUpdate but semantically more clear for outside components.
  handleModelValueUpdate(item)
}

defineExpose({ focus, blur, selectItem, setForceOpenState, highlightFirstOption })

watch(query, () => {
  if (!selectedItem.value) {
    if (
      filteredItems.value.length === 1 &&
      query.value.toLowerCase() === getItemValue(filteredItems.value[0]).toLowerCase() &&
      props.autoSelectOnExactMatch
    ) {
      // If there is an exact match, auto-select it.
      selectedItem.value = filteredItems.value[0]
      handleModelValueUpdate(selectedItem.value, true)
    } else if (props.returnStringIfNoMatches) {
      if (!isProgrammaticQueryUpdate.value) {
        // Emit an update only if the query was not changed programmatically.
        // Otherwise, when the user clicks the combobox button and then selects an option,
        // the selected item would be emitted first (expected) — and then the query value would
        // mistakenly be emitted here as well.
        emit("update:modelValue", query.value)
      }
    }
  }
  if (!isProgrammaticQueryUpdate.value) {
    emit("queryChanged", query.value)
  }
  isProgrammaticQueryUpdate.value = false
})

watch(
  () => query.value.length,
  (val) => {
    if (props.inactivateActiveOptionOnEmptyQuery) {
      // If query is empty, and character length went from >0 to ==0, disable all items.
      isAllOptionsDisabled.value = val === 0
    }
  },
  { immediate: true }
)

const forceOpen = ref(false)
const isOpen = ref(false)
function captureOpen(open: boolean) {
  // Hack to capture open state from combobox internals
  isOpen.value = open
  return false
}

// Allows finer control over the combobox options list open state
const isOpenComputed = computed(() => forceOpen.value || isOpen.value)

// Scroll the combobox options into view
watch(isOpenComputed, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      const optionsEl = comboboxOptions.value?.$el
      if (!optionsEl) return
      // Scroll into view
      if ("scrollIntoViewIfNeeded" in Element.prototype) {
        // The scrollIntoViewIfNeeded method is a non-standard and deprecated feature.. but it does what we want!
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoViewIfNeeded
        optionsEl.scrollIntoViewIfNeeded(false)
      } else {
        optionsEl.scrollIntoView({ block: "nearest", behavior: "instant" })
      }
      comboboxOptionsHeight.value = optionsEl.offsetHeight + 4
    })
  }
})

watch(filteredItems, () => {
  if (isOpenComputed.value) {
    nextTick(() => {
      const optionsEl = comboboxOptions.value?.$el
      if (!optionsEl) return
      comboboxOptionsHeight.value = optionsEl.offsetHeight + 4
    })
  }
})

// Below code is used to prevent the headless combobox's default behaviour where it selects the highlighted item even
// when the user clicks outside the combobox. This is not the desired behaviour in our case.
const comboboxButton = useTemplateRef("comboboxButton")
const { isOutside: isOutsideComboboxOptions } = useMouseInElement(comboboxOptions)
const { isOutside: isOutsideOptionsButton } = useMouseInElement(comboboxButton)
const wasClickedOutside = ref(false)
useEventListener("mousedown", () => {
  // Fires before onModelValueUpdate().
  // wasClickedOutside is evaluated in onModelValueUpdate() to prevent selecting the highlighted item when clicking
  // outside the combobox.
  // Added isOutsideOptionsButton to prevent bug PNTS-1647.
  wasClickedOutside.value =
    hasFocus.value && isOutsideComboboxOptions.value && isOutsideOptionsButton.value

  // Reset the flag after the next tick.
  setTimeout(() => {
    wasClickedOutside.value = false
  })
})

async function onModelValueUpdate(val: any) {
  if (!props.beforeModelValueUpdateHandler(val)) {
    selectedItem.value = null
    return
  }

  // User clicked outside the combobox, so we should not select the currently highlighted item.
  if (wasClickedOutside.value) {
    selectedItem.value = null
    return
  }

  if (query.value !== "") {
    // Normal situation. There is query text present and the user either uses tab, enter or clicks on an option.
    handleModelValueUpdate(val)
    return
  }

  // Special situation. There is no query text present, but the user has moused over an option to select it, possibly
  // inadvertently. A subsequent tab press should not select the option.

  // Start by deselecting the item.
  selectedItem.value = null

  // Await nextTick because else keyDown and lmbDown flags have not yet been set.
  await nextTick()

  if (query.value === "" && (enterKeyDown.value || optionLmbDown.value)) {
    // If the user explicitly selected an option (enter key or mouse click), we should run the logic anyway.
    // Pressing the tab key will not fire this logic.
    handleModelValueUpdate(val)
  }
}

const isProgrammaticQueryUpdate = ref(false)
function handleModelValueUpdate(val: any, preventBlur = false) {
  if (slots["custom-options"] && val instanceof CustomComboboxOptionWrapper) {
    selectedItem.value = null
    val.onSelect(val.item)
    return
  }

  if (props.creationOption && val === false) {
    // create option is selected.
    initiateNewItemCreation()
    return
  }

  if (val) {
    emit("update:modelValue", val)

    // Set query to match with selected object value. Otherwise, when selecting the text and pressing a character,
    // the query value would not be cleared and replaced with the selected character as one would expect (bug).
    isProgrammaticQueryUpdate.value = true // Set flag to prevent watch(query) from triggering.
    query.value = getItemValue(val)

    emit("itemSelected", val)

    if (!preventBlur) {
      // Since we selected an item, we can blur the input now.
      setTimeout(blur)
    }
  }
}

watch(
  isOpenComputed,
  (val) => {
    emit("comboboxOptionsOpenChange", val)
  },
  { immediate: true }
)

watch(
  () => props.modelValue,
  (val) => {
    /* In case prop is set to null from parent. */
    if (!val && val !== selectedItem.value) {
      query.value = ""
    }
    /* In case prop value is set to an object. For example, suggestion object (in parent) is clicked. */
    if (typeof val === "object" && val !== selectedItem.value) {
      selectedItem.value = val
    }
  }
)

/* When items are loaded, try to select item based on modelValue. */
watch(
  () => props.items,
  () => {
    const match = props.items.filter((item: any) => item[props.itemsKeyName] === props.modelValue)
    if (match.length === 1) {
      selectedItem.value = match[0]
    }
  }
)

watch(hasFocus, (val) => {
  if (!val && selectedText.value.length > 0) {
    document.getSelection()?.removeAllRanges()
  }
})

function highlightFirstOption() {
  // Hacky way to force re-render of ComboboxOptions, causing the first item in the list to be active.
  comboboxOptionsKey.value = Math.random()
}

watch(() => filteredItems.value.length, highlightFirstOption)

onMounted(() => {
  if (props.modelValue) {
    selectedItem.value = props.modelValue
  }
  if (props.creationOption && !props.creationModelType) {
    throw new Error("If creationOption is true, creationModelType must be set.")
  }
  if (props.autoFocus) {
    focus()
  }
})

watch(
  () => [props.itemsValueName, props.itemsValueFunction],
  ([nameVal, fnVal]) => {
    if (!nameVal && !fnVal) {
      throw new Error("Either itemsValueName or itemsValueFunction must be set.")
    } else if (nameVal && fnVal) {
      throw new Error("Only one of itemsValueName or itemsValueFunction can be set.")
    }
  },
  { immediate: true }
)

useEventListener("mouseup", () => {
  optionLmbDown.value = false // In some cases the flag is not reset, so we force it here.
})

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<style scoped></style>
